import $ from 'jquery';

import KeyUtils from 'chairisher/util/key';
import Menu from 'chairisher/component/menu';

import createDomElement from 'chairisher/util/dom';

import { highlightNonMatchingQueryText } from 'chairisher/util/format';
import { isBlog } from 'chairisher/context/site';
import { isFunctionType } from 'chairisher/util/type';

const { KeyCodes } = KeyUtils;

/**
 * @callback responseFilterCallback
 * @param {Array.<Choice>} choices The choices to filter
 * @returns {Array.<Choice>} The filtered choices
 */

/**
 * Autocomplete queries a given endpoint and presents the available choices via a simple dropdown menu.
 *
 * @param {Object} settings Object of settings used to customize the Autocomplete instance
 * @param {jQuery=} settings.$menuContainerEl @see this.$menuContainerEl
 * @param {jQuery} settings.$queryEl @see this.$queryEl
 * @param {boolean} settings.areOnlyLeafNodesSelectable @see this.areOnlyLeafNodesSelectable
 * @param {Object=} settings.dataObject @see this.dataObject
 * @param {string} settings.dataEndpoint @see this.dataEndpoint
 * @param {boolean=} settings.isGlobalSearch @see this.isGlobalSearch
 * @param {boolean=} settings.isMultiSelect @see this.isMultiSelect
 * @param {boolean=} settings.menuChoiceHasThumbnail @see this.menuChoiceHasThumbnail
 * @param {string=} settings.menuChoiceContentTemplateSelector @see this.menuChoiceContentTemplateSelector
 * @param {string=} settings.menuElClasses @see this.menuElClasses
 * @param {number=} settings.placeholderIndex @see this.placeholderIndex
 * @param {responseFilterCallback=} settings.responseFilterCallback @see this.responseFilterCallback
 * @param {boolean=} settings.shouldBoldNonQueryTextResults @see this.shouldBoldNonQueryTextResults
 * @param {boolean=} settings.shouldHideAutocompleteText @see this.shouldHideAutocompleteText
 * @param {boolean=} settings.shouldSubmitFormOnEnter @see this.shouldSubmitFormOnEnter
 * @param {boolean=} settings.shouldTabAndEnterComplete @see this.shouldTabAndEnterComplete
 *
 * @constructor
 * @class Autocomplete
 */
const Autocomplete = function Autocomplete(settings) {
    const options = $.extend(
        {
            $menuContainerEl: this.$menuContainerEl,
            $queryEl: this.$queryEl,
            areOnlyLeafNodesSelectable: this.areOnlyLeafNodesSelectable,
            dataDisplayKey: this.dataDisplayKey,
            dataObject: {},
            dataEndpoint: this.dataEndpoint,
            isGlobalSearch: this.isGlobalSearch,
            isMultiSelect: this.isMultiSelect,
            limit: this.limit,
            menuChoiceContentTemplateSelector: this.menuChoiceContentTemplateSelector,
            menuChoiceHasThumbnail: this.menuChoiceHasThumbnail,
            menuElClasses: this.menuElClasses,
            minQueryLength: this.minQueryLength,
            placeholderIndex: this.placeholderIndex,
            responsefilterCallback: this.responseFilterCallback,
            shouldBoldNonQueryTextResults: this.shouldBoldNonQueryTextResults,
            shouldHideAutocompleteText: this.shouldHideAutocompleteText,
            shouldSubmitFormOnEnter: this.shouldSubmitFormOnEnterFlag,
            shouldTabAndEnterComplete: this.shouldTabAndEnterCompleteFlag,
        },
        settings,
    );

    this.$queryEl = options.$queryEl;
    this.$menuContainerEl = options.$menuContainerEl;

    this.areOnlyLeafNodesSelectable = options.areOnlyLeafNodesSelectable;
    this.dataObject = options.dataObject;
    this.dataDisplayKey = options.dataDisplayKey;
    this.dataEndpoint = options.dataEndpoint;
    this.isGlobalSearch = options.isGlobalSearch;
    this.isMultiSelect = options.isMultiSelect;
    this.limit = options.limit;
    this.menuChoiceContentTemplateSelector = options.menuChoiceContentTemplateSelector;
    this.menuChoiceHasThumbnail = !!options.menuChoiceHasThumbnail;
    this.menuElClasses = options.menuElClasses;
    this.minQueryLength = options.minQueryLength;
    this.placeholderIndex = options.placeholderIndex;
    this.placeholderText = this.$queryEl.attr('placeholder');
    this.responseFilterCallback = options.responseFilterCallback;
    this.shouldBoldNonQueryTextResults = options.shouldBoldNonQueryTextResults;
    this.shouldHideAutocompleteText = options.shouldHideAutocompleteText;
    this.shouldSubmitFormOnEnterFlag = options.shouldSubmitFormOnEnter;
    this.shouldTabAndEnterCompleteFlag = options.shouldTabAndEnterComplete;

    this.render();
    this.bind();
};

/**
 * jQuery element for storing the "X" clear search button
 *
 * @type {jQuery}
 */
Autocomplete.prototype.$clearSearchBtn = null;

/**
 * jQuery element that will serve as a wrapper for the query input
 *
 * @type {jQuery}
 */
Autocomplete.prototype.$el = null;

/**
 * Optional jQuery element to append the menu to; if a function, it must return a jQuery object
 *
 * @type {jQuery|function}
 */
Autocomplete.prototype.$menuContainerEl = null;

/**
 * jQuery element where queries can be input
 *
 * @type {jQuery}
 */
Autocomplete.prototype.$queryEl = null;

/**
 * Flag that indicates if all nodes are selectable or just leaf nodes.
 *
 * @type {boolean}
 * @default
 */
Autocomplete.prototype.areOnlyLeafNodesSelectable = false;

/**
 * The key found in a choice whose value should be displayed in the query field upon selection
 *
 * @type {string}
 * @default
 */
Autocomplete.prototype.dataDisplayKey = 'display';

/**
 * The endpoint on the server that returns autocomplete choices
 *
 * @type {string}
 */
Autocomplete.prototype.dataEndpoint = null;

/**
 * Object that contains GET parameters (excluding query) to pass in to `this.dataEndpoint`
 *
 * @type {Object}
 */
Autocomplete.prototype.dataObject = null;

/**
 * Flag that indicates if choices came back from the server
 *
 * @type {boolean}
 * @default
 */
Autocomplete.prototype.hasChoicesFlag = false;

/**
 * Flag that indicates if autocomplete is part of the global search
 *
 * @type {boolean}
 * @default
 */
Autocomplete.prototype.isGlobalSearch = false;

/**
 * Flag that indicates if the choices are multi-select or not
 *
 * @type {boolean}
 */
Autocomplete.prototype.isMultiSelect = true;

/**
 * Limits the number of results autocomplete will have
 *
 * @type {number}
 * @default
 */
Autocomplete.prototype.limit = null;

/**
 * The timer id that gets set before firing an ajax request
 *
 * @type {number|undefined}
 */
Autocomplete.prototype.keyTimerId = null;

/**
 * The most recent query string found in the `.js-autocomplete-query` element
 *
 * @type {string}
 */
Autocomplete.prototype.lastQueryString = '';

/**
 * Instance of a menu used to display choices
 *
 * @type {Menu}
 */
Autocomplete.prototype.menu = null;

/**
 * Selector of the element that contains the template to use when marshaling menu choices
 *
 * @type {string}
 * @default
 */
Autocomplete.prototype.menuChoiceContentTemplateSelector = '#js-template-menu-choice-content';

/**
 * Flag that indicates if menu choices should expect to display a thumbnail image or not
 *
 * @type {boolean}
 * @default
 */
Autocomplete.prototype.menuChoiceHasThumbnail = true;

/**
 * Classes to assign the menu DOM element
 *
 * @type {string}
 * @default
 */
Autocomplete.prototype.menuElClasses = 'menu';

/**
 * Used to traverse the DOM to select the autocompleter's placeholder element
 *
 * @type {string}
 * @default
 */
Autocomplete.prototype.placeHolderElSelector = '.js-autocomplete-placeholder';

/**
 * Placeholder text for the `.js-autocomplete-query` element
 *
 * @type {string}
 */
Autocomplete.prototype.placeholderText = '';

/**
 * Placeholder index for items in dropdown menu
 *
 * @type {number}
 */
Autocomplete.prototype.placeholderIndex = 0;

/**
 * Used to traverse the DOM to select the autocompleter's query element
 *
 * @type {string}
 * @default
 */
Autocomplete.prototype.queryElSelector = '.js-autocomplete-query';

/**
 * A callback to execute that filters data sent back from the server
 *
 * @type {responseFilterCallback}
 */
Autocomplete.prototype.responseFilterCallback = null;

/**
 * Flag that indicates whether the autocomplete is enabled to make queries
 *
 * @type {boolean}
 * @default
 */
Autocomplete.prototype.areQueriesEnabledFlag = true;

/**
 * Default length of queryString required before a query can be attempted
 *
 * @type {number}
 * @default
 */
Autocomplete.prototype.minQueryLength = 2;

/**
 * Flag that indicates whether query result that don't match should be bold
 *
 * @type {boolean}
 * @default
 */
Autocomplete.prototype.shouldBoldNonQueryTextResults = false;

/**
 * Flag that indicates whether the autocomplete text should be hidden
 *
 * @type {boolean}
 * @default
 */
Autocomplete.prototype.shouldHideAutocompleteText = false;

/**
 * Flag that indicates whether pressing enter on a field will submit the form
 *
 * @type {boolean}
 * @default
 */
Autocomplete.prototype.shouldSubmitFormOnEnterFlag = false;

/**
 * Flag that indicates whether pressing tab or enter on a field will fill it out with the first dropdown option
 *
 * @type {boolean}
 * @default
 */
Autocomplete.prototype.shouldTabAndEnterCompleteFlag = false;

/**
 * @returns {boolean} True indicates there is enough info available to make a query
 */
Autocomplete.prototype.canMakeQuery = function canMakeQuery() {
    const queryLength = this.getQueryText()?.length;
    return queryLength && queryLength >= this.minQueryLength;
};

/**
 * @returns {Menu} The Menu instance attached directly to this component
 */
Autocomplete.prototype.getMenu = function getMenu() {
    return this.menu;
};

/**
 * @returns {jQuery} The jQuery element that is used as a placeholder
 */
Autocomplete.prototype.getPlaceholderEl = function getPlaceholderEl() {
    return this.$placeholderEl;
};

/**
 * @returns {jQuery} The jQuery element that is used for inputting queries
 */
Autocomplete.prototype.getQueryEl = function getQueryEl() {
    return this.$queryEl;
};

/**
 * @returns {string} The text entered into the query input
 */
Autocomplete.prototype.getQueryText = function getQueryText() {
    return this.getQueryEl().val();
};

/**
 * @returns {boolean} True indicates choices are available for selection
 */
Autocomplete.prototype.hasChoices = function hasChoices() {
    return this.hasChoicesFlag;
};

/**
 * @param {string} queryText The text to insert in the query element
 */
Autocomplete.prototype.setQueryText = function setQueryText(queryText) {
    this.getQueryEl().val(this.sanitizeQueryString(queryText));
};

/**
 * @returns {boolean} True indicates the query input has text (including spaces) entered in it
 */
Autocomplete.prototype.hasQueryText = function hasQueryText() {
    return !!this.getQueryText().length;
};

/**
 * Hides the placeholder element and resets its text back to its default
 */
Autocomplete.prototype.hidePlaceholder = function hidePlaceholder() {
    this.getPlaceholderEl().val('');
};

/**
 * @returns {boolean} True indicates the `.js-autocomplete-placeholder` element is visible
 */
Autocomplete.prototype.isPlaceholderVisible = function isPlaceholderVisible() {
    return !this.getPlaceholderEl().hasClass('hidden');
};

/**
 * Removes strong tags and quotation marks from query string
 *
 * @param {string} queryText A display string that will be used in the search bar
 * @returns {string} A string that has no strong tags or quotation marks
 */
Autocomplete.prototype.sanitizeQueryString = function sanitizeQueryString(queryText) {
    return queryText.replace(/<strong>|<\/strong>|"/gi, '');
};

/**
 * Displays the placeholder element unless text is present in the query element
 */
Autocomplete.prototype.showPlaceholder = function showPlaceholder() {
    this.getPlaceholderEl().val(this.placeholderText);
};

/**
 * Disable queries while still allowing input.
 */
Autocomplete.prototype.disableQueries = function disableQueries() {
    this.areQueriesEnabledFlag = false;
};

/**
 * Enable queries without affecting ability to input text.
 */
Autocomplete.prototype.enableQueries = function enableQueries() {
    this.areQueriesEnabledFlag = true;
};

/**
 * Disable the autocomplete, preventing all queries.
 */
Autocomplete.prototype.disable = function disable() {
    this.disableQueries();
    this.$el.addClass('disabled');
    this.$el.find('input').prop('disabled', true);
};

/**
 * Enable the autocomplete, allowing queries.
 */
Autocomplete.prototype.enable = function enable() {
    this.enableQueries();
    this.$el.removeClass('disabled');
    this.$el.find('input').prop('disabled', false);
};

/**
 * Whether the autocomplete is enabled to make queries.
 *
 * @returns {boolean}
 */
Autocomplete.prototype.areQueriesEnabled = function areQueriesEnabled() {
    return this.areQueriesEnabledFlag;
};

/**
 * Whether the autocomplete is enabled to submit the form when a user presses enter.
 *
 * @returns {boolean}
 */
Autocomplete.prototype.shouldSubmitFormOnEnter = function shouldSubmitFormOnEnter() {
    return this.shouldSubmitFormOnEnterFlag;
};

/**
 * Whether the autocomplete is enabled to fill in the input with the first menu item when a user presses tab or
 * enter.
 *
 * @returns {boolean}
 */
Autocomplete.prototype.shouldTabAndEnterComplete = function shouldTabAndEnterComplete() {
    return this.shouldTabAndEnterCompleteFlag;
};

/**
 * Toggles the placeholder element's visibility. If text is present in the query element the placeholder will not
 * be displayed regardless of what is passed in.
 *
 * @param {boolean} shouldDisplay Optional flag indicating if the placeholder should be displayed or not
 */
Autocomplete.prototype.togglePlaceholder = function togglePlaceholder(shouldDisplay) {
    if (shouldDisplay) {
        this.showPlaceholder();
    } else {
        this.hidePlaceholder();
    }
};

/**
 * Binds actions to the various elements that comprise the Autocompleter
 *
 */
Autocomplete.prototype.bind = function bind() {
    const $queryEl = this.getQueryEl();

    $queryEl.closest('.js-form-group').on('click', (e) => {
        if (!$(e.target).is('input')) {
            // Clicking on an input triggers focus without any help.
            $queryEl.trigger('focus');
        }
    });

    $queryEl.on(
        'blur',
        $.proxy(function onBlur(e) {
            this.$clearSearchBtn.removeClass('has-focus');
            if (this.shouldTabAndEnterComplete()) {
                if (this.menu.getChoiceEls().eq(0).text() !== this.getQueryEl().val()) {
                    this.setQueryText('');
                    this.menu.updateSuggestionChoices([]);
                    this.trigger(e, 'autocomplete:blur');
                }
            } else {
                this.trigger(e, 'autocomplete:blur');
            }

            this.togglePlaceholder(!this.hasQueryText());
        }, this),
    );

    $queryEl.on(
        'autocomplete:blur',
        $.proxy(function onAutocompleteBlur() {
            window.setTimeout(
                $.proxy(function hideMenuAfterBlur() {
                    this.menu.hide();
                }, this),
                100,
            );
        }, this),
    );

    $queryEl.on(
        'focus',
        $.proxy(function onFocus(e) {
            this.$clearSearchBtn.addClass('has-focus');
            this.trigger(e, 'autocomplete:focus');
            if (!this.menu.isVisible() && this.menu.getSuggestionChoices().length) {
                this.menu.show();
            }
        }, this),
    );

    $queryEl.on(
        'keydown',
        $.proxy(function onKeydown(e) {
            this.trigger(e, 'autocomplete:keydown');

            const keyCode = e.which;

            // If there is text in the query element:
            //     - Down Arrow opens the menu if there are choices and it is not already opened
            //     - Up and Down Arrow scrolls through the choices is there are any and the menu is open
            //     - Right Arrow selects the first choice if the menu is open, there are menu choices, and none are selected
            //     - Left arrow key doesn't do anything special by default
            if (KeyUtils.isArrow(keyCode) && this.hasQueryText()) {
                if (this.menu.isVisible()) {
                    let newIndex = null;
                    const $selectedChoice = this.menu.getSelectedChoiceEl();
                    const hasSelectedChoice = $selectedChoice.length;

                    if (keyCode === KeyCodes.ArrowDown || keyCode === KeyCodes.ArrowUp) {
                        newIndex = 0;
                        if (hasSelectedChoice) {
                            newIndex =
                                this.menu.getIndexForEl($selectedChoice) + (keyCode === KeyCodes.ArrowUp ? -1 : 1);
                        }
                    } else if (keyCode === KeyCodes.ArrowRight && !hasSelectedChoice) {
                        newIndex = 0;
                    }

                    if (newIndex !== null) {
                        this.menu.deselectChoiceEl();
                        this.menu.makeSelection(newIndex);
                        this.hidePlaceholder();

                        const $menuEl = this.menu.getMenuEl();
                        const menuHeight = $menuEl.height();
                        const menuScrollTop = $menuEl.scrollTop();

                        const $selectedEl = this.menu.getSelectedChoiceEl();
                        const selectedEl = $selectedEl[0];
                        const selectedElHeight = $selectedEl.height();
                        const selectedElOffsetTop = selectedEl.offsetTop;

                        let newOffset = null;

                        if (selectedElOffsetTop + selectedElHeight > menuHeight + menuScrollTop) {
                            newOffset = selectedElOffsetTop - menuHeight + selectedElHeight;
                        } else if (selectedElOffsetTop < menuScrollTop) {
                            newOffset = selectedElOffsetTop;
                        }

                        if (newOffset !== null) {
                            $menuEl.scrollTop(newOffset);
                        }

                        const selectedChoice = this.menu.getSuggestionChoices()[this.menu.getIndexForEl($selectedEl)];
                        this.setQueryText(selectedChoice[this.dataDisplayKey].trim());
                    }
                } else if (keyCode === KeyCodes.ArrowDown && this.menu.getSuggestionChoices().length) {
                    this.menu.show();
                }
            } else if (keyCode === KeyCodes.Enter || keyCode === KeyCodes.Tab) {
                let $selected = this.menu.getSelectedChoiceEl();
                if (this.shouldTabAndEnterComplete() && !$selected.length) {
                    $selected = this.menu.getChoiceEls().eq(0);
                }
                if ($selected.length) {
                    this.setQueryText($selected.text());
                    // clients should handle the `autocomplete:selected` event if they want something to happen
                    // other than the input being filled in with the selected value...
                    e.preventDefault();

                    const selected = this.menu.getSuggestionChoices()[this.menu.getIndexForEl($selected)];

                    this.trigger(e, 'autocomplete:selected', [selected, $selected.index('.js-menu-choice')]);

                    if (this.shouldSubmitFormOnEnter() && keyCode === KeyCodes.Enter) {
                        this.trigger(e, 'autocomplete:submit');
                    }

                    if (keyCode === KeyCodes.Tab) {
                        this.menu.updateSuggestionChoices([selected]);
                        this.menu.hide();
                        this.menu.clearAllSelections();
                    }
                }
            }
        }, this),
    );

    $queryEl.on(
        'keypress',
        $.proxy(function onKeypress(e) {
            this.trigger(e, 'autocomplete:keypress');
        }, this),
    );

    $queryEl.on(
        'keyup input',
        $.proxy(function onKeyupInput(e) {
            const keyCode = e.which;
            const eventType = e.type;
            const queryText = this.getQueryText().trim();

            this.$clearSearchBtn.toggleClass('hidden', !this.hasQueryText());
            this.trigger(e, `autocomplete:${eventType}`);

            if ($.inArray(keyCode, [KeyCodes.Escape, KeyCodes.Enter, KeyCodes.Tab]) !== -1) {
                this.menu.hide();

                // hide suggestion text if there is any, otherwise
                // mimic default placeholder text functionality...
                this.togglePlaceholder(!this.hasQueryText());
                return;
            }

            if (queryText && this.isPlaceholderVisible()) {
                this.hidePlaceholder();
            }

            const shouldAttemptFetch =
                this.areQueriesEnabled() &&
                ((eventType === 'input' && queryText.length) ||
                    (keyCode === KeyCodes.ArrowDown && !this.menu.isVisible()) ||
                    (!KeyUtils.isArrow(keyCode) && queryText !== this.lastQueryString) ||
                    (!KeyUtils.isArrow(keyCode) && $.inArray(keyCode, [KeyCodes.OS_Key1, KeyCodes.OS_Key2]) === -1));

            this.lastQueryString = queryText;

            if (shouldAttemptFetch) {
                if (this.canMakeQuery()) {
                    // throttle the number of AJAX requests by setting a timer:
                    //
                    // when the timer expires execute the last pending AJAX request, but if
                    // another non-arrow key has been pressed before then, cancel the existing
                    // timer and restart the clock before sending the request

                    this.clearOutgoingRequests();

                    this.keyTimerId = window.setTimeout(
                        $.proxy(function onFetchTimer() {
                            if (!this.canMakeQuery()) {
                                return; // avoid subtle timing issues...
                            }

                            this.fetchData();
                        }, this),
                        300,
                    );
                } else {
                    this.menu.hide();
                    this.menu.updateSuggestionChoices([]);
                    if (!this.hasQueryText()) {
                        this.showPlaceholder();
                    }
                }
            }
        }, this),
    );

    this.$clearSearchBtn.on('click', () => {
        this.$clearSearchBtn.addClass('hidden');
        this.setQueryText('');
        this.menu.hide();
        this.menu.updateSuggestionChoices([]);
        $queryEl.trigger('focus');
    });

    this.menu.getMenuEl().on(
        'menuchoice:selected',
        $.proxy(function onMenuChoiceSelected(e, data) {
            this.setQueryText(data[this.dataDisplayKey]);
            this.menu.updateSuggestionChoices([data]);
            this.hidePlaceholder();
            if (!this.isMultiSelect) {
                this.menu.hide();
            }
            this.trigger(e, 'autocomplete:selected', data);
        }, this),
    );
};

/**
 * Fetches data from the server at a given endpoint for a given entity type
 *
 * @returns {$.Deferred}
 */
Autocomplete.prototype.fetchData = function fetchData() {
    this.trigger($.Event(), 'autocomplete:search');
    const additionalData = { query: this.getQueryText() };
    if (!isBlog()) {
        additionalData.path_name = document.location.pathname;
    }
    const dataObject = { ...this.dataObject, ...additionalData };

    return $.ajax({
        data: dataObject,
        url: this.dataEndpoint,
    }).done(
        $.proxy(function onFetchDataDone(data) {
            this.keyTimerId = undefined;

            let { choices } = data;

            if (isFunctionType(this.responseFilterCallback)) {
                choices = this.responseFilterCallback(data.choices);
            }

            if (this.limit && choices.length) {
                choices = choices.slice(0, this.limit);
            }

            if (this.shouldBoldNonQueryTextResults) {
                const queryText = this.getQueryText();
                for (let i = 1; i < choices.length; i += 1) {
                    choices[i].display = highlightNonMatchingQueryText(queryText, choices[i].display);
                }
            }

            this.menu.updateSuggestionChoices(choices);
            this.menu.toggle(choices.length);

            this.hasChoicesFlag = !!choices.length;

            // if there are choices, combine the query and placeholder text to
            // suggest the first choice with the appearance of the query being
            // completed by the placeholder...
            if (this.hasChoices()) {
                const queryText = this.getQueryText();

                const placeholderIndex = this.placeholderIndex >= choices.length ? 0 : this.placeholderIndex;

                const choiceText = choices[placeholderIndex][this.dataDisplayKey];

                const sanitizedChoiceText = this.sanitizeQueryString(choiceText);

                const trimmedChoiceText = sanitizedChoiceText.toLowerCase().trim();
                const trimmedQueryText = queryText.toLowerCase().trim();

                if (trimmedChoiceText.indexOf(trimmedQueryText) === 0) {
                    this.getPlaceholderEl().val(queryText + sanitizedChoiceText.substring(queryText.length));

                    if (!this.isPlaceholderVisible()) {
                        this.showPlaceholder();
                    }
                }
                this.menu.getMenuEl().scrollTop(0);
            } else {
                const noResultsEventType = 'autocomplete:noresults';
                this.trigger($.Event(noResultsEventType), noResultsEventType, {
                    query: this.getQueryText(),
                });
            }
        }, this),
    );
};

/**
 * @param {string} key The key in the data object to update
 * @param {string|number|Array|Object} value The value to set
 */
Autocomplete.prototype.updateDataObject = function updateDataObject(key, value) {
    this.dataObject[key] = value;
};

/**
 * Clears the timer that keeps track of when to send requests, effectively canceling them
 *
 */
Autocomplete.prototype.clearOutgoingRequests = function clearOutgoingRequests() {
    if (this.keyTimerId !== undefined) {
        window.clearTimeout(this.keyTimerId);
    }
};

/**
 * Renders the widget. If the plugin is being used on a text input, then
 * marshal the appropriate structure and replace the text field with the new structure...
 *
 */
Autocomplete.prototype.render = function render() {
    // ensure the query input is housed in a wrapper element and has a
    // placeholder element as a sibling so we can provide autocompleted suggestions...
    const wrapperDiv = createDomElement('div', {
        classes: ['autocomplete-input', 'js-autocomplete-input'],
    });
    this.$el = $(wrapperDiv);

    const placeholderInput = createDomElement('input', {
        classes: ['autocomplete-placeholder', 'js-autocomplete-placeholder'],
        attributes: {
            'aria-hidden': 'true',
            'readonly': 'readonly',
            'tabindex': '-1', // prevent the placeholder input from being focusable
        },
    });
    this.$placeholderEl = $(placeholderInput);

    const $queryEl = this.getQueryEl();
    this.$el.append(this.getPlaceholderEl());
    this.$el.insertAfter($queryEl);
    $queryEl.detach();
    this.$el.append($queryEl);

    this.$clearSearchBtn = this.$el.siblings('.js-autocomplete-btn-container').find('.js-btn-clear-search');
    this.$clearSearchBtn.toggleClass('hidden', !$queryEl.val());

    this.menu = new Menu({
        areOnlyLeafNodesSelectable: this.areOnlyLeafNodesSelectable,
        dataDisplayKey: this.dataDisplayKey,
        isGlobalSearch: this.isGlobalSearch,
        isMultiSelect: this.isMultiSelect,
        menuChoiceContentTemplateSelector: this.menuChoiceContentTemplateSelector,
        menuChoiceHasThumbnail: this.menuChoiceHasThumbnail,
        menuElClasses: this.menuElClasses,
    });
    this.menu.hide();

    if (this.$menuContainerEl) {
        const $containerEl = isFunctionType(this.$menuContainerEl) ? this.$menuContainerEl() : this.$menuContainerEl;
        $containerEl.append(this.menu.getMenuEl());
    } else {
        this.$el.append(this.menu.getMenuEl());
    }

    // ensure that that placeholder element mirrors various essential styles
    // that are found on the query input so autocompleted suggestions line up properly...
    const cssPropertiesToCopy = {};
    $.each(
        [
            'background',
            'border',
            'font-family', // need to expand font to keep firefox happy
            'font-size', // need to expand font to keep firefox happy
            'font-style', // need to expand font to keep firefox happy
            'font-weight', // need to expand font to keep firefox happy
            'letter-spacing',
            'line-height',
            'margin-bottom', // need to expand margin to keep firefox happy in some instances...
            'margin-left',
            'margin-right',
            'margin-top',
            'padding-bottom', // need to expand padding to keep firefox happy in some instances...
            'padding-left',
            'padding-right',
            'padding-top',
            'text-transform',
        ],
        (i, prop) => {
            cssPropertiesToCopy[prop] = $queryEl.css(prop);
        },
    );

    cssPropertiesToCopy.height = '100%';

    // move attributes to placeholder
    this.getPlaceholderEl()
        .css(cssPropertiesToCopy)
        .val($queryEl.val() || this.placeholderText);

    const queryElCss = {
        position: 'relative',
    };

    if (this.shouldHideAutocompleteText) {
        this.$placeholderEl.addClass('hidden');
    } else {
        queryElCss.background = 'transparent';
    }

    $queryEl.css(queryElCss).attr({
        autocomplete: 'off',
        spellcheck: false,
        dir: 'auto',
    });

    if (this.isGlobalSearch) {
        $queryEl.attr({
            autocorrect: 'off',
        });
    }
};

/**
 * Triggers a new event while passing along the original event and any other additional data provided
 *
 * @param {jQuery.Event} e The original jQuery event
 * @param {string} type The new event's type
 * @param {Object=} data Additional data to be passed along as an argument to the event handler
 */
Autocomplete.prototype.trigger = function trigger(e, type, data) {
    const newEvent = $.extend({}, e); // clone so we don't modify the original event object
    newEvent.type = type; // overwrite e.type before creating the event since e.type overrides the 1st arg type arg
    const event = $.Event(type, newEvent);

    this.getQueryEl().trigger(event, data);
};

export default Autocomplete;
